import {
  buildCartAddItems,
  clampPositiveQuantity,
  discountedRollTotalCents,
  formatMarketMoney
} from "./nd-commerce-rules.js?v=20260922-per-unit-rounding";

const FORM_SELECTOR = 'form[action="/cart/add"]';
const ADD_BUTTON_SELECTOR = 'button[name="add"]';

function queryRequired(root, selector) {
  const node = root.querySelector(selector);
  if (!node) {
    throw new Error(`Missing required fleece add-on node: ${selector}`);
  }
  return node;
}

function getQty(node) {
  return clampPositiveQuantity(node.textContent);
}

function setButtonSubmitting(button, isSubmitting, originalText) {
  if (!button) return;
  button.disabled = isSubmitting;
  const addingText = button.closest('[data-nd-fleece-addon]')?.dataset.addingLabel || '…';
  button.textContent = isSubmitting ? addingText : originalText;
}

export function initFleeceRollAddon(root) {
  if (!root || root.dataset.initialized === "true") return root;

  root.dataset.initialized = "true";

  const optionOnetime = queryRequired(root, '[data-addon-option="onetime"]');
  const optionSubscribe = queryRequired(root, '[data-addon-option="subscribe"]');
  const onetimeControls = queryRequired(root, '[data-addon-controls="onetime"]');
  const subscribeControls = queryRequired(root, '[data-addon-controls="subscribe"]');
  const onetimeQtyValue = queryRequired(root, '[data-addon-qty-value="onetime"]');
  const subscribeQtyValue = queryRequired(root, '[data-addon-qty-value="subscribe"]');
  const onetimePrice = queryRequired(root, '[data-addon-price="onetime"]');
  const subscribePrice = queryRequired(root, '[data-addon-price="subscribe"]');
  const frequencySelect = queryRequired(root, '[data-addon-frequency]');
  const rollsPerDelivery = queryRequired(root, '[data-addon-rolls-per-delivery]');
  const onetimeRolls = root.querySelector("[data-addon-rolls-onetime]");

  const options = [optionOnetime, optionSubscribe];
  const onetimeVariantId = root.dataset.onetimeVariantId;
  const subscribeVariantId = root.dataset.subscribeVariantId;
  const onetimeUnitCents = Number.parseInt(root.dataset.onetimeUnitCents || "0", 10) || 0;
  const subscribeUnitCents = Number.parseInt(root.dataset.subscribeUnitCents || "0", 10) || 0;
  const rollsPerSubscriptionItem =
    Number.parseInt(root.dataset.rollsPerSubscriptionItem || "2", 10) || 2;
  const missingVariantMessage = root.dataset.errorMissingVariant || "Please select a product option.";
  const addToCartErrorMessage =
    root.dataset.errorAddToCart || "We couldn’t add this item to your cart. Please try again.";
  const formatPrice = (cents) =>
    formatMarketMoney(cents, {
      currencyCode: root.dataset.moneyCurrency || "USD",
      locale: root.dataset.moneyLocale || document.documentElement.lang || window.Shopify?.locale
    });
  let selectedAddon = root.dataset.defaultAddon || "subscribe";
  let isSubmitting = false;

  const dispatchAddonChange = () => {
    let addonTotalCents = 0;

    if (selectedAddon === "onetime") {
      addonTotalCents = discountedRollTotalCents(onetimeUnitCents, getQty(onetimeQtyValue));
    } else if (selectedAddon === "subscribe") {
      addonTotalCents = subscribeUnitCents * getQty(subscribeQtyValue);
    }

    document.dispatchEvent(
      new CustomEvent("nd:fleece-addon-change", {
        detail: {
          productHandle: root.dataset.productHandle || "",
          selectedAddon,
          addonTotalCents
        }
      })
    );
  };

  const updateOnetimePrice = () => {
    const qty = getQty(onetimeQtyValue);
    if (onetimeRolls) {
      onetimeRolls.textContent = (onetimeRolls.dataset.template || "__COUNT__ rolls")
        .replace("__COUNT__", String(qty * rollsPerSubscriptionItem));
    }
    const subtotal = onetimeUnitCents * qty;
    const discounted = discountedRollTotalCents(onetimeUnitCents, qty);

    if (qty >= 3) {
      onetimePrice.innerHTML =
        `<span class="addon-price-compare">${formatPrice(subtotal)}</span>${formatPrice(discounted)}`;
      return;
    }

    onetimePrice.textContent = formatPrice(discounted);
  };

  const updateRollsPerDelivery = () => {
    const quantity = getQty(subscribeQtyValue);
    const count = quantity * rollsPerSubscriptionItem;
    subscribePrice.textContent = formatPrice(subscribeUnitCents * quantity);
    const interval = frequencySelect.selectedOptions[0]?.dataset.deliveryInterval || "monthly";
    const template =
      interval === "quarterly"
        ? rollsPerDelivery.dataset.quarterlyTemplate || "__COUNT__ rolls every 3 months"
        : rollsPerDelivery.dataset.monthlyTemplate || "__COUNT__ rolls every month";
    rollsPerDelivery.textContent = template.replace("__COUNT__", String(count));
  };

  const syncSelectionUi = () => {
    options.forEach((option) => {
      option.classList.toggle("selected", option.dataset.addonOption === selectedAddon);
    });
    onetimeControls.classList.toggle("visible", selectedAddon === "onetime");
    subscribeControls.classList.toggle("visible", selectedAddon === "subscribe");
  };

  const findMainQuantityInput = (form) => {
    const direct = form.querySelector('input[name="quantity"]');
    if (direct) return direct;

    const formId = form.getAttribute("id");
    if (!formId) return null;

    return document.querySelector(`input[name="quantity"][form="${formId}"]`);
  };

  const findMainSellingPlanInput = (form) => {
    const direct = form.querySelector('input[name="selling_plan"]');
    if (direct) return direct;

    const formId = form.getAttribute("id");
    if (!formId) return null;

    return document.querySelector(`input[name="selling_plan"][form="${formId}"]`);
  };

  const buildItems = (form) => {
    const mainVariantInput = form.querySelector('input[name="id"]');
    if (!mainVariantInput) {
      throw new Error(missingVariantMessage);
    }

    const mainQuantityInput = findMainQuantityInput(form);
    const mainSellingPlanInput = findMainSellingPlanInput(form);

    return buildCartAddItems({
      mainVariantId: mainVariantInput.value,
      mainQuantity: mainQuantityInput ? mainQuantityInput.value : 1,
      mainSellingPlanId: mainSellingPlanInput ? mainSellingPlanInput.value : "",
      addonMode: selectedAddon,
      addonVariantId: selectedAddon === "subscribe" ? subscribeVariantId : onetimeVariantId,
      addonQuantity:
        selectedAddon === "subscribe" ? getQty(subscribeQtyValue) : getQty(onetimeQtyValue),
      addonSellingPlanId: frequencySelect.value
    });
  };

  const openCartDrawer = async () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || typeof cartDrawer.renderContents !== "function") {
      window.location.href = "/cart";
      return;
    }

    const response = await fetch("/cart?sections=cart-drawer,cart-icon-bubble");
    const sections = await response.json();
    cartDrawer.renderContents({ sections });
    cartDrawer.classList.remove("is-empty");
  };

  const submitWithAddon = async (form, submitButton) => {
    if (isSubmitting) return;
    isSubmitting = true;
    const originalText = submitButton ? submitButton.textContent : "";

    try {
      setButtonSubmitting(submitButton, true, originalText);

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: buildItems(form) })
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.description || addToCartErrorMessage);
      }

      await response.json();
      await openCartDrawer();
    } catch (error) {
      console.error("Add-on cart error:", error);
      alert(error.message || addToCartErrorMessage);
    } finally {
      isSubmitting = false;
      setButtonSubmitting(submitButton, false, originalText);
    }
  };

  const bindForms = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      if (form.dataset.ndFleeceAddonBound === "true") return;
      form.dataset.ndFleeceAddonBound = "true";

      form.addEventListener(
        "submit",
        (event) => {
          const submitter = event.submitter;
          if (submitter && submitter.name !== "add") return;
          if (!submitter && document.activeElement?.getAttribute("name") !== "add") return;

          event.preventDefault();
          event.stopImmediatePropagation();
          submitWithAddon(form, submitter || form.querySelector(ADD_BUTTON_SELECTOR));
        },
        true
      );
    });

    document.querySelectorAll(`${FORM_SELECTOR} ${ADD_BUTTON_SELECTOR}`).forEach((button) => {
      if (button.dataset.ndFleeceAddonBound === "true") return;
      button.dataset.ndFleeceAddonBound = "true";
      button.addEventListener(
        "click",
        (event) => {
          const form = button.form || button.closest(FORM_SELECTOR);
          if (!form) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          submitWithAddon(form, button);
        },
        true
      );
    });
  };

  // Adjusting an option's quantity or schedule selects that option.
  const selectAddon = (mode) => {
    if (selectedAddon === mode) return;
    selectedAddon = mode;
    syncSelectionUi();
  };

  options.forEach((option) => {
    option.addEventListener("click", (event) => {
      // Only real controls opt out; any other click toggles the option.
      if (event.target.closest("button, select, a, input")) return;
      selectedAddon =
        selectedAddon === option.dataset.addonOption ? "none" : option.dataset.addonOption;
      syncSelectionUi();
      dispatchAddonChange();
    });
  });

  root.querySelectorAll("[data-addon-qty-button]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      const mode = button.getAttribute("data-addon-mode");
      const targetNode = mode === "subscribe" ? subscribeQtyValue : onetimeQtyValue;
      const direction = button.getAttribute("data-addon-qty-button");
      const nextValue =
        direction === "+"
          ? getQty(targetNode) + 1
          : Math.max(1, getQty(targetNode) - 1);

      targetNode.textContent = String(nextValue);
      if (mode === "onetime") {
        selectAddon("onetime");
        updateOnetimePrice();
      } else {
        selectAddon("subscribe");
        updateRollsPerDelivery();
      }
      dispatchAddonChange();
    });
  });

  frequencySelect.addEventListener("change", () => {
    selectAddon("subscribe");
    updateRollsPerDelivery();
    dispatchAddonChange();
  });

  updateOnetimePrice();
  updateRollsPerDelivery();
  syncSelectionUi();
  bindForms();
  dispatchAddonChange();

  return root;
}

function autoInit() {
  document.querySelectorAll("[data-nd-fleece-addon]").forEach((root) => {
    initFleeceRollAddon(root);
  });
}

if (typeof window !== "undefined") {
  window.NDStorefront = window.NDStorefront || {};
  window.NDStorefront.initFleeceRollAddon = initFleeceRollAddon;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit, { once: true });
  } else {
    autoInit();
  }
}
